'use client'

import { useRouter } from 'next/navigation'
import { useSearch } from '@/src/contexts'
import { useContext } from 'react'

import { theme, Typography } from 'antd'

import { topSearchedSpecialtiesData } from '@/src/data'
import Image from 'next/image'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'
import { trackSearchEvents } from '@/src/lib'

const { Text } = Typography
interface Card {
  icon: string
  title: string
  specialty: string
}

export const TopSearchedSpecialties = () => {
  const router = useRouter()
  const {
    token: { colorPrimary },
  } = theme.useToken()
  const { query, setQuery, setCurrentSearchBarCategory } = useSearch()
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  const handleSpecialtyCardClick = ({ card }: { card: Card }) => {
    // Amplitude: Track the specialty card click
    trackSearchEvents.specialtyCardClicked(trackAmplitudeEvent)({
      'Specialty Title': card.title,
      'Specialty Value': card.specialty,
    })

    setQuery({
      ...query,
      singleValueParameters: {
        key: 'specialty',
        value: card.specialty,
      },
    })
    setCurrentSearchBarCategory('unified')
    router.push('/search')
  }

  return (
    <div className='mt-8 lg:mt-16'>
      <Text style={{ color: colorPrimary, fontWeight: 500, fontSize: 22 }}>
        Or explore popular searches on PhysicianGPS:
      </Text>
      <div className='w-full mt-4'>
        <div className='flex flex-wrap justify-between gap-4'>
          {topSearchedSpecialtiesData.map((card: Card, index: number) => (
            <div
              key={index}
              onClick={() => handleSpecialtyCardClick({ card })}
              data-testid={`specialty-card-${card.title}`}
              className='w-[calc(33.33%-1rem)] aspect-square transform transition duration-300 hover:scale-105 cursor-pointer'
            >
              <div
                data-testid={`specialty-container-${card.title}`}
                className='h-full text-center rounded-xl flex flex-col gap-2 justify-between items-center py-1 px-3 lg:p-6 bg-gradient-to-br from-[#F5F3EE] to-[#E8E5DD] hover:from-[#E8E5DD] hover:to-[#D8D4C8] shadow-md hover:shadow-lg transition-all duration-300 border border-[#BDA772] group'
              >
                <div className='flex-1 w-4/5 flex items-center justify-center'>
                  <div className='aspect-square w-full bg-gradient-to-br from-white to-gray-100 rounded-full shadow-md group-hover:shadow-lg transition-all duration-300 overflow-hidden flex items-center justify-center'>
                    <div className='w-[70%] h-[70%] relative transform group-hover:scale-110 transition-transform duration-300'>
                      <Image
                        src={`/assets/icons/${card.icon}`}
                        alt={`${card.title} icon`}
                        fill
                        sizes='(max-width: 768px) 33vw, 20vw'
                        style={{ objectFit: 'contain' }}
                        priority={index < 6}
                        data-testid={`specialty-image-${card.title}`}
                        className='filter group-hover:brightness-110 transition-all duration-300'
                      />
                    </div>
                  </div>
                </div>
                <div className='w-full'>
                  <h2
                    data-testid={`specialty-title-${card.title}`}
                    className='text-sm md:text-lg font-medium  text-[#1A202C] group-hover:text-[#000000] transition-colors duration-300'
                  >
                    {card.title}
                  </h2>
                  <div className='w-0 h-1 bg-[#BDA772] mx-auto rounded-full opacity-0 group-hover:w-24 group-hover:opacity-75 transition-all duration-300'></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
